import { CSSProperties, FC, type ReactNode } from 'react';
import cx from 'classnames';
import { DownloadButtonProps } from '@/types/download/DownloadButtonProps';
import DownloadDataButton from '../../DownloadDataButton/DownloadDataButton';
import TableActionBar from './TableActionBar/TableActionBar';
import styles from '../Table.module.scss';

const TABLE_TOOLBAR_SEARCH_WIDTH = '30rem';
const TABLE_TOOLBAR_SEARCH_MAX_WIDTH = '30rem';

interface TableToolbarProps {
  downloadButtonProps?: DownloadButtonProps;
  showToolbarBorder: boolean;
  showAdjustTable: boolean;
  onAdjustTableClick: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchClear?: () => void;
  searchPlaceholder?: string;
  toolbarElement?: ReactNode;
  headerElement?: ReactNode;
  filterSlot?: ReactNode;
  filterSlotWidth?: CSSProperties['width'];
  additionalActionBarElement?: ReactNode;
}

const TableToolbar: FC<TableToolbarProps> = ({
  downloadButtonProps,
  showToolbarBorder,
  showAdjustTable,
  onAdjustTableClick,
  searchValue,
  onSearchChange,
  onSearchClear,
  searchPlaceholder,
  toolbarElement,
  headerElement,
  filterSlot,
  filterSlotWidth,
  additionalActionBarElement,
}: TableToolbarProps) => {
  const shouldShowSearch = searchValue !== undefined || onSearchChange !== undefined || onSearchClear !== undefined;
  const hasHeaderElement = !!headerElement;
  const hasHeaderActions = !!downloadButtonProps || !!toolbarElement;
  const shouldRenderHeaderRow = hasHeaderElement || hasHeaderActions;
  const shouldRenderActionBar = shouldShowSearch || showAdjustTable || !!filterSlot;
  const showBorderLine =
    (shouldRenderHeaderRow && shouldRenderActionBar) ||
    (showToolbarBorder && (hasHeaderElement || shouldRenderActionBar));

  const downloadButton = downloadButtonProps ? (
    <DownloadDataButton
      {...downloadButtonProps}
      className={cx(styles.downloadTableButton, downloadButtonProps.className)}
      data-testid="download-button"
    />
  ) : null;

  return (
    <>
      {shouldRenderHeaderRow && (
        <div
          className={cx(styles.tableToolbar, {
            [styles.toolbarWithHeader]: hasHeaderElement,
          })}
        >
          {hasHeaderElement && <div className={styles.toolbarHeaderElement}>{headerElement}</div>}
          {hasHeaderActions && (
            <div className={styles.toolbarHeaderActions}>
              {downloadButton}
              {toolbarElement}
            </div>
          )}
        </div>
      )}
      {showBorderLine && <div className={styles.toolbarBorderLine} />}
      {shouldRenderActionBar && (
        <TableActionBar
          showSearch={shouldShowSearch}
          searchValue={searchValue}
          searchPlaceholder={searchPlaceholder}
          searchWidth={TABLE_TOOLBAR_SEARCH_WIDTH}
          searchMaxWidth={TABLE_TOOLBAR_SEARCH_MAX_WIDTH}
          filterSlotWidth={filterSlotWidth}
          onSearchChange={onSearchChange}
          onSearchClear={onSearchClear}
          filterSlot={filterSlot}
          showAdjustTable={showAdjustTable}
          onAdjustTableClick={onAdjustTableClick}
          className={styles.tableToolbarActionBar}
          additionalActionBarElement={additionalActionBarElement}
          data-testid="table-toolbar"
        />
      )}
    </>
  );
};

export default TableToolbar;
